import React, { useState, useMemo } from "react";
import { Popover, Typography, Box } from "@mui/material";
import IconComponent from "@CubeComponents/icons";
import { getFilterPredicates } from "./Modals/FilterModal";
import type {
  SortCondition,
  GroupCondition,
  FilterCondition,
  AppliedFilterCondition,
  GridRowBase,
  ValidatedColumnDefinition,
} from "./index";

export type VirtualAnchorElement = {
  getBoundingClientRect: () => {
    top: number;
    left: number;
    bottom: number;
    right: number;
    width: number;
    height: number;
    x: number;
    y: number;
    toJSON: () => void;
  };
};

interface OptionDropDownProps {
  readonly anchorEl: { top: number; left: number } | null;
  readonly handleClose: () => void;
  readonly setModalState: React.Dispatch<React.SetStateAction<string>>;
  readonly setModalAnchor: React.Dispatch<React.SetStateAction<HTMLElement | VirtualAnchorElement | null>>;
  readonly setGroupConditions: React.Dispatch<React.SetStateAction<GroupCondition[]>>;
  readonly setSortConditions: React.Dispatch<React.SetStateAction<SortCondition[]>>;
  readonly setFilterConditions?: React.Dispatch<React.SetStateAction<AppliedFilterCondition[]>>;
  readonly setAppliedFilters?: React.Dispatch<React.SetStateAction<string[]>>;
  readonly setFilterPredicates?: React.Dispatch<React.SetStateAction<string | Record<string, unknown>>>;
  readonly setFilterConditionsObject?: React.Dispatch<React.SetStateAction<Record<string, FilterCondition>>>;
  /** Null while the grid has no columns yet - SlickGrid1's own prop is `... [] | null`. */
  readonly columnDefinitions: ValidatedColumnDefinition<GridRowBase>[] | null;
  readonly currentColumnId: string;
  readonly pinnedColumns: string[];
  readonly sortConditions?: SortCondition[];
  readonly groupConditions?: GroupCondition[];
  readonly filterConditions?: Record<string, FilterCondition>;
  readonly onChange: (option: Option) => void;
  enableFilteringOption: boolean;
  enableSortingOption: boolean;
  enableGroupingOption: boolean;
  enableColumnChooser: boolean;
  hasCustomColumnChooser?: boolean;
  gridId?: string;
}

interface Option {
  id: number;
  label: string;
  icon: React.ReactNode;
  isActive?: boolean;
}

function OptionDropDown({
  anchorEl,
  handleClose,
  setModalState,
  setModalAnchor,
  currentColumnId,
  pinnedColumns,
  onChange,
  setGroupConditions,
  setSortConditions,
  setFilterConditions,
  setAppliedFilters,
  setFilterPredicates,
  setFilterConditionsObject,
  columnDefinitions,
  sortConditions = [],
  groupConditions = [],
  filterConditions = {},
  enableFilteringOption = false,
  enableSortingOption = false,
  enableGroupingOption = false,
  enableColumnChooser = false,
  hasCustomColumnChooser = false,
  gridId,
}: OptionDropDownProps) {
  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  const isColumnPinned = pinnedColumns.includes(currentColumnId);

  // ✅ Helper function to remove index suffix from field names
  const removeLastIndex = (field: string) => {
    return field.replace(/_\d+$/, "") || field;
  };

  // Check if current column has active filters, sorts, or groups
  const hasActiveSort = sortConditions.some((s) => s.column === currentColumnId);
  const hasActiveGroup = groupConditions.some((g) => g.column === currentColumnId);

  // ✅ Fixed: Check both the key and the id property, and handle index suffixes
  const hasActiveFilter = Object.keys(filterConditions).some((key) => {
    const cleanKey = removeLastIndex(key);
    const filterValue = filterConditions[key] as FilterCondition | undefined;
    const cleanId = filterValue?.id ? removeLastIndex(filterValue.id) : null;

    return cleanKey === currentColumnId || cleanId === currentColumnId;
  });

  const renderOptions = useMemo(() => {
    const col = columnDefinitions?.find((col) => col.id === currentColumnId);

    let options: Option[] = [];

    // Sort option - replace with Clear Sort if active
    if (enableSortingOption && !col?.disableSorting) {
      if (hasActiveSort) {
        options.push({
          id: 101,
          label: "Remove Sort",
          icon: <IconComponent name="Sort" height="1rem" width="1rem" />,
          isActive: true,
        });
      } else {
        options.push({
          id: 1,
          label: "Sort",
          icon: <IconComponent name="Sort" height="1rem" width="1rem" />,
          isActive: false,
        });
      }
    }

    // Filter option - replace with Clear Filter if active
    if (enableFilteringOption && !col?.disableFiltering) {
      if (hasActiveFilter) {
        options.push({
          id: 102,
          label: "Remove Filter",
          icon: <IconComponent name="Filter" height="1rem" width="1rem" />,
          isActive: true,
        });
      } else {
        options.push({
          id: 2,
          label: "Filter",
          icon: <IconComponent name="Filter" height="1rem" width="1rem" />,
          isActive: false,
        });
      }
    }

    // Group option - replace with Clear Group if active
    if (enableGroupingOption && !col?.disableGrouping) {
      if (hasActiveGroup) {
        options.push({
          id: 103,
          label: "Remove Group",
          icon: <IconComponent name="Grouping" height="1rem" width="1rem" />,
          isActive: true,
        });
      } else {
        options.push({
          id: 3,
          label: "Group",
          icon: <IconComponent name="Grouping" height="1rem" width="1rem" />,
          isActive: false,
        });
      }
    }

    // Hide option
    if (enableColumnChooser || hasCustomColumnChooser) {
      options.push({
        id: 4,
        label: "Hide",
        icon: <IconComponent name="View" height="1rem" width="1rem" />,
      });
    }

    // Pin/Unpin option
    if (isColumnPinned) {
      options.push({
        id: 5,
        label: "Unpin",
        icon: <IconComponent name="Pin" height="1rem" width="1rem" />,
      });
    } else {
      options.push({
        id: 6,
        label: "Pin",
        icon: <IconComponent name="Pin" height="1rem" width="1rem" />,
      });
    }

    return options;
  }, [
    columnDefinitions,
    currentColumnId,
    isColumnPinned,
    hasActiveSort,
    hasActiveFilter,
    hasActiveGroup,
    enableColumnChooser,
    enableFilteringOption,
    enableGroupingOption,
    enableSortingOption,
    hasCustomColumnChooser,
  ]);

  const handleClearFilter = () => {
    // Clear from conditions array (FilterModal's internal state)
    if (setFilterConditions) {
      setFilterConditions((prev) => {
        return prev.filter((c) => {
          const baseField = removeLastIndex(c.field);
          return baseField !== currentColumnId;
        });
      });
    }

    if (setAppliedFilters) {
      setAppliedFilters((prev) => {
        return prev.filter((f) => f !== currentColumnId);
      });
    }

    // Clear from filterConditionsObject and recalculate filter predicates
    if (setFilterConditionsObject) {
      setFilterConditionsObject((prev) => {
        const updated = { ...prev };

        for (const key of Object.keys(updated)) {
          const cleanKey = removeLastIndex(key);
          const entry = updated[key] as FilterCondition | undefined;
          const cleanId = entry?.id ? removeLastIndex(entry.id) : null;

          if (cleanKey === currentColumnId || cleanId === currentColumnId) {
            delete updated[key];
          }
        }

        if (setFilterPredicates) {
          const newPredicates = getFilterPredicates(updated);
          setFilterPredicates(newPredicates);
        }

        return updated;
      });
    }
  };

  const handleSelect = (option: Option) => {
    setSelectedValue(option.id);

    // Handle clear actions
    if (option.label === "Remove Sort") {
      setSortConditions((prev) => prev.filter((s) => s.column !== currentColumnId));
      handleClose();
      return;
    }

    if (option.label === "Remove Filter") {
      handleClearFilter();
      handleClose();
      return;
    }

    if (option.label === "Remove Group") {
      setGroupConditions((prev) => prev.filter((g) => g.column !== currentColumnId));
      handleClose();
      return;
    }

    // Original functionality
    onChange(option);

    if (option.label === "Hide") {
      handleClose();
      return;
    }

    let toolbarIconElement: HTMLElement | null = null;

    switch (option.label) {
      case "Sort":
        toolbarIconElement = document.getElementById(gridId ? `sorting-icon-${gridId}` : "sorting-icon");
        break;
      case "Filter":
        toolbarIconElement = document.getElementById(gridId ? `filtering-icon-${gridId}` : "filtering-icon");
        break;
      case "Group":
        toolbarIconElement = document.getElementById(gridId ? `grouping-icon-${gridId}` : "grouping-icon");
        break;
      default:
        break;
    }

    if (toolbarIconElement) {
      const rect = toolbarIconElement.getBoundingClientRect();
      const offset = 25;
      setModalAnchor({
        getBoundingClientRect: () => ({
          top: rect.bottom + window.scrollY - offset,
          left: rect.left + window.scrollX,
          bottom: rect.bottom + window.scrollY - offset,
          right: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
          x: rect.left + window.scrollX,
          y: rect.bottom + window.scrollY - offset,
          toJSON: () => {},
        }),
      });
    }

    setModalState(option.label);

    if (option.label === "Group") {
      setGroupConditions((prev) => {
        if (prev.some((c) => c.column === currentColumnId)) return prev;
        return [...prev, { column: currentColumnId, direction: "asc" }];
      });
    } else if (option.label === "Sort") {
      setSortConditions((prev) => {
        if (prev.some((c) => c.column === currentColumnId)) return prev;
        return [...prev, { column: currentColumnId, direction: "asc" }];
      });
    }

    handleClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorReference="anchorPosition"
      anchorPosition={anchorEl || { top: 0, left: 0 }}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        zIndex: 1500,
        "& .MuiPaper-root": {
          boxShadow: "0px 0.25rem 0.75rem rgba(0, 0, 0, 0.1)",
          width: "fit-content",
          marginTop: "0.625rem",
          border: "1px solid #DCE8F5",
        },
      }}
    >
      {renderOptions.map((option, index) => (
        <Box
          key={option.id}
          onClick={() => handleSelect(option)}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            height: "3rem",
            gap: "1rem",
            borderBottom: index === renderOptions.length - 1 ? "none" : "1px solid #DCE8F5",
            backgroundColor: selectedValue === option.id ? "#E6F0FF" : "transparent",
            "&:hover": {
              backgroundColor: selectedValue === option.id ? "#E6F0FF" : "#F5F5F5",
            },
          }}
        >
          <Box sx={{ position: "relative" }}>
            {option.icon}
            {option.isActive && (
              <Box
                sx={{
                  position: "absolute",
                  top: "-0.125rem",
                  insetInlineEnd: "-0.25rem",
                  width: "0.5rem",
                  height: "0.5rem",
                  backgroundColor: "var(--primary-gradient-color1)",
                  borderRadius: "50%",
                  border: "1px solid white",
                }}
              />
            )}
          </Box>
          <Typography variant="body2">{option.label}</Typography>
        </Box>
      ))}
    </Popover>
  );
}

export default OptionDropDown;
